import { useAuthContext } from '@/auth/useAuthContext';
import { CustomerCombobox } from '@/components/CustomerCombobox';
import { DatePicker } from '@/components/DatePicker';
import { PetCombobox } from '@/components/PetCombobox';
import { RunPicker } from '@/components/RunPicker';
import { ServiceCard } from '@/components/ServiceCard';
import { VaccinePills } from '@/components/VaccinePills';
import { createBookingGroup, createWaitlistEntry, listServices, sendGroupToKiosk } from '@/lib/api';
import type { Customer, PetWithVaccinations, ServiceCatalogueItem } from '@/lib/types';
import { cn } from '@/lib/utils';
import { AlertTriangle, Loader2, Plus, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface PetSlot {
  slotKey: string;
  petId: string | null;
  pet: PetWithVaccinations | null;
  runId: string | null;
  feeding: string;
  medication: string;
  selectedServiceIds: string[];
}

let slotCounter = 0;
const emptySlot = (): PetSlot => ({
  slotKey: `slot-${++slotCounter}`,
  petId: null,
  pet: null,
  runId: null,
  feeding: '',
  medication: '',
  selectedServiceIds: [],
});

export function BookingsNew() {
  const { state } = useAuthContext();
  const navigate = useNavigate();

  const facility = state.me?.activeFacility ?? state.me?.facilitiesInScope[0] ?? null;

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [pets, setPets] = useState<PetSlot[]>([emptySlot()]);
  const [checkInDate, setCheckInDate] = useState('');
  const [checkInTime, setCheckInTime] = useState('14:00');
  const [checkoutDate, setCheckoutDate] = useState('');
  const [checkoutTime, setCheckoutTime] = useState('10:00');
  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addToWaitlist, setAddToWaitlist] = useState(false);

  const facilityId = facility?.id;
  useEffect(() => {
    if (facilityId) {
      listServices(facilityId)
        .then(setServices)
        .catch(() => {});
    }
  }, [facilityId]);

  // Coarse role check — owner/manager can override expired vaccines.
  // Phase 4: replace with capability check from /api/me.
  const canOverride = useMemo(() => {
    const roleId = state.me?.activeMembership?.roleId;
    return roleId === 'owner' || roleId === 'manager';
  }, [state]);

  const setPet = (i: number, patch: Partial<PetSlot>) =>
    setPets((arr) => arr.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const removePet = (i: number) => setPets((arr) => arr.filter((_, idx) => idx !== i));
  const addPet = () => setPets((arr) => [...arr, emptySlot()]);

  // Build ISO timestamps in facility-local time (not UTC midnight).
  // Format: "2026-05-22T14:00:00" without a Z/offset suffix so the server
  // interprets it as facility-local. The API stores times as ISO 8601 text
  // (UTC), so the server side must apply the facility timezone for display.
  // Sending bare local datetime avoids the UTC-midnight shift that would
  // occur for non-UTC tenants if we appended "Z".
  const checkIn = checkInDate && checkInTime ? `${checkInDate}T${checkInTime}:00` : '';
  const checkout = checkoutDate && checkoutTime ? `${checkoutDate}T${checkoutTime}:00` : '';

  const blockingErrors = ((): string[] => {
    const errs: string[] = [];
    if (!customer) errs.push('Select or create a customer');
    if (!facility) errs.push('No facility selected');
    if (pets.some((p) => !p.petId)) errs.push('Each pet section needs a pet');
    if (!checkIn || !checkout) errs.push('Set check-in and checkout');
    if (checkIn && checkout && checkout <= checkIn) errs.push('Checkout must be after check-in');
    if (!canOverride) {
      const hasExpired = pets.some((p) => p.pet?.vaccinations.some((v) => !v.valid));
      if (hasExpired) errs.push('A required vaccine has expired — manager override needed');
    }
    return errs;
  })();

  const canSubmit = blockingErrors.length === 0;

  const submit = async (sendToKiosk: boolean) => {
    if (!canSubmit || !customer || !facility) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        customerId: customer.id,
        facilityId: facility.id,
        checkIn,
        checkout,
        pets: pets
          .filter((p): p is PetSlot & { petId: string } => p.petId !== null)
          .map((p) => ({
            petId: p.petId,
            runId: p.runId,
            feeding: p.feeding || null,
            medication: p.medication || null,
            serviceLineItems: p.selectedServiceIds.map((serviceId) => {
              const svc = services.find((s) => s.id === serviceId);
              const checkoutDateObj = new Date(checkout);
              checkoutDateObj.setUTCDate(checkoutDateObj.getUTCDate() - 1);
              return {
                serviceId,
                scheduledDate: checkoutDateObj.toISOString().slice(0, 10),
                priceMinor: svc?.priceMinor ?? svc?.defaultPriceMinor ?? 0,
              };
            }),
          })),
      };
      const { groupId, bookings: created } = await createBookingGroup(body);
      if (sendToKiosk) await sendGroupToKiosk(groupId);

      // Optionally also add to the waitlist (the booking is confirmed, but
      // the customer has been noted as wanting an earlier/alternative slot).
      if (addToWaitlist) {
        const firstPet = pets.find((p) => p.petId !== null);
        if (firstPet?.petId) {
          try {
            await createWaitlistEntry({
              facilityId: facility.id,
              customerId: customer.id,
              petId: firstPet.petId,
              requestedCheckIn: checkIn,
              requestedCheckout: checkout,
              notes: 'Added from new booking form',
            });
          } catch {
            // Non-fatal — booking already created, just skip the waitlist silently
          }
        }
      }

      // Navigate to today-view so staff see the booking immediately in context.
      // A dedicated /bookings/:id detail view doesn't exist yet (sub-spec 06
      // follow-up); /today is the operational home for newly-created stays.
      const firstBookingId = created[0]?.id;
      navigate(firstBookingId ? `/customers/${body.customerId}` : '/today');
    } catch (e: unknown) {
      const apiErr = e as { message?: string };
      setError(apiErr.message ?? 'Failed to save booking');
    } finally {
      setBusy(false);
    }
  };

  if (!facility) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-10">
        <p className="text-muted-foreground">No facility selected.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-10 md:px-10 md:py-14">
      <header className="mb-10">
        <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
          Records · New booking
        </p>
        <h1 className="font-heading text-4xl font-medium tracking-tight md:text-5xl">
          New boarding booking
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          for {state.me?.activeAccount?.name} · {facility.name}
        </p>
      </header>

      <Section title="Customer">
        <CustomerCombobox value={customer} onChange={setCustomer} />
      </Section>

      <Section title="Pets">
        <div className="space-y-6">
          {pets.map((slot, i) => (
            <PetSection
              key={slot.slotKey}
              slot={slot}
              index={i}
              showRemove={pets.length > 1}
              customer={customer}
              services={services}
              facilityId={facility.id}
              onChange={(patch) => setPet(i, patch)}
              onRemove={() => removePet(i)}
            />
          ))}
          <button
            type="button"
            onClick={addPet}
            disabled={!customer}
            className="flex items-center gap-1.5 text-sm text-primary press disabled:opacity-50"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            Add another pet
          </button>
        </div>
      </Section>

      <Section title="Dates">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <DatePicker label="Check-in date" value={checkInDate} onChange={setCheckInDate} />
          <TimeField label="Check-in time" value={checkInTime} onChange={setCheckInTime} />
          <DatePicker label="Checkout date" value={checkoutDate} onChange={setCheckoutDate} />
          <TimeField label="Checkout time" value={checkoutTime} onChange={setCheckoutTime} />
        </div>
      </Section>

      {/* Waitlist toggle */}
      {customer && checkIn && checkout && (
        <label className="mb-6 flex cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-surface px-5 py-3.5 text-sm">
          <input
            type="checkbox"
            checked={addToWaitlist}
            onChange={(e) => setAddToWaitlist(e.target.checked)}
            className="rounded border-border"
          />
          <span>
            <span className="font-medium">Add to waitlist</span>
            <span className="ml-1 text-muted-foreground">
              — the booking will be saved and the customer also added to the waitlist for an earlier
              slot
            </span>
          </span>
        </label>
      )}

      {blockingErrors.length > 0 && (
        <div className="mb-6 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3">
          <AlertTriangle
            className="mt-0.5 size-4 shrink-0 text-warning-foreground"
            strokeWidth={1.75}
          />
          <ul className="space-y-1 text-sm text-warning-foreground">
            {blockingErrors.map((e) => (
              <li key={e}>· {e}</li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      <div className="sticky bottom-0 -mx-6 flex flex-col gap-2 border-t border-border bg-background/85 p-4 backdrop-blur-md md:static md:mx-0 md:flex-row md:border-0 md:bg-transparent md:p-0">
        <button
          type="button"
          onClick={() => void submit(true)}
          disabled={!canSubmit || busy}
          className={cn(
            'flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground press',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {busy && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
          Send to kiosk and save
        </button>
        <button
          type="button"
          onClick={() => void submit(false)}
          disabled={!canSubmit || busy}
          className="flex-1 rounded-lg border border-border bg-surface px-4 py-3 text-sm font-medium press disabled:opacity-50"
        >
          Save without sending
        </button>
        <button
          type="button"
          onClick={() => navigate('/tasks')}
          className="rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground press"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (page-local)
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8 rounded-2xl bg-surface p-6 ring-1 ring-border elevate md:p-8">
      <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {title}
      </p>
      {children}
    </section>
  );
}

interface PetSectionProps {
  slot: PetSlot;
  index: number;
  showRemove: boolean;
  customer: Customer | null;
  services: ServiceCatalogueItem[];
  facilityId: string;
  onChange: (patch: Partial<PetSlot>) => void;
  onRemove: () => void;
}

function PetSection({
  slot,
  index,
  showRemove,
  customer,
  services,
  facilityId,
  onChange,
  onRemove,
}: PetSectionProps) {
  return (
    <div
      className={cn(
        'relative rounded-xl border border-border bg-background p-4',
        index > 0 && 'mt-2',
      )}
    >
      {showRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute right-3 top-3 grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary press"
          aria-label="Remove pet"
        >
          <X className="size-3.5" strokeWidth={1.75} />
        </button>
      )}
      <p className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        Pet {index + 1}
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PetCombobox
          customerId={customer?.id ?? null}
          value={slot.pet}
          onChange={(p) => onChange({ pet: p, petId: p?.id ?? null })}
        />
        <RunPicker
          facilityId={facilityId}
          value={slot.runId}
          onChange={(runId) => onChange({ runId })}
          species={slot.pet?.species}
        />
      </div>
      {slot.pet && <VaccinePills vaccinations={slot.pet.vaccinations} className="mt-3" />}
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Feeding instructions">
          <textarea
            value={slot.feeding}
            onChange={(e) => onChange({ feeding: e.target.value })}
            className="w-full rounded-lg border border-border bg-surface p-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
            rows={3}
          />
        </Field>
        <Field label="Medication">
          <textarea
            value={slot.medication}
            onChange={(e) => onChange({ medication: e.target.value })}
            className="w-full rounded-lg border border-border bg-surface p-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
            rows={3}
          />
        </Field>
      </div>
      <div className="mt-4">
        <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Optional services
        </p>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {services
            .filter((s) => {
              const allowed = JSON.parse(s.speciesAllowed || '[]') as string[];
              if (slot.pet && !allowed.includes(slot.pet.species)) return false;
              const applicable = JSON.parse(s.applicableTo || '{}') as Record<string, unknown>;
              return applicable.boarding_addon !== false;
            })
            .map((s) => (
              <ServiceCard
                key={s.id}
                service={s}
                selected={slot.selectedServiceIds.includes(s.id)}
                onToggle={() =>
                  onChange({
                    selectedServiceIds: slot.selectedServiceIds.includes(s.id)
                      ? slot.selectedServiceIds.filter((x) => x !== s.id)
                      : [...slot.selectedServiceIds, s.id],
                  })
                }
              />
            ))}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = `timefield-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <Field label={label} htmlFor={id}>
      <input
        id={id}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="num h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
      />
    </Field>
  );
}
